import { createFileRoute } from "@tanstack/react-router";

import AudiolabSection from "@/components/marketing/AudiolabSection";
import FaqSection from "@/components/marketing/FaqSection";
import HowItWorks from "@/components/marketing/HowItWorks";
import { marketingHead } from "@/components/marketing/marketingHead";
import {
  Accent,
  MarketingSection,
  SectionKicker,
  SectionTitle,
} from "@/components/marketing/MarketingSection";
import ProductHero from "@/components/marketing/ProductHero";
import RelatedProducts from "@/components/marketing/RelatedProducts";
import SellOverviewSection from "@/components/marketing/SellOverviewSection";
import Footer from "@/components/nav/Footer";

export const Route = createFileRoute("/labels")({
  component: LabelsPage,
  head: () =>
    marketingHead({
      title: "Record Label Software & Release Workflow Automation | Lutely",
      description:
        "Prepare tracks, create release assets, organize metadata, manage multiple artists, and sell music directly to fans from one workspace.",
      path: "/labels",
    }),
});

const SCREENS = [
  { src: "/marketing/labels/audiolab-audio.webp", alt: "Lutely tracks view" },
  {
    src: "/marketing/labels/release-management.webp",
    alt: "Lutely release management screen",
  },
];

const FAQ = [
  {
    question: "Can one account manage multiple artists?",
    answer:
      "Yes. Label workspaces are designed to organize catalogs and releases across multiple artists.",
  },
  {
    question: "Does Lutely distribute music to DSPs?",
    answer:
      "Lutely prepares and manages releases and supports direct-to-fan sales. It does not currently replace your DSP distributor.",
  },
  {
    question: "Can labels upload catalogs in bulk?",
    answer:
      "Yes. Bulk upload is designed for higher-volume catalog and release workflows.",
  },
  {
    question: "Can each artist sell directly to fans?",
    answer:
      "Yes. Releases can be sold through Lutely with commission-free artist sales and connected payout workflows.",
  },
];

function LabelsPage() {
  return (
    <main className="flex flex-col bg-white">
      <ProductHero
        photo="/marketing/labels/artist-studio.webp"
        title={
          <>
            <Accent>Automate release workflows</Accent> across your roster.
          </>
        }
        lede="Prepare tracks, create release assets, organize metadata, manage multiple artists, and sell music directly to fans from one workspace."
        cta="Explore Lutely for Labels"
        proof={["Built for multiple artists", "Bulk catalog workflows"]}
      />
      <HowItWorks
        title="More releases without more busy work."
        intro="One connected workflow for every artist on your roster."
        steps={[
          {
            title: "Upload across your roster",
            body: "Bring in tracks individually or in bulk and keep them organized by artist.",
          },
          {
            title: "Prepare your tracks",
            body: "Lyrics and stems are created automatically. Use connected tools for clean versions, masters, artwork, and videos.",
          },
          {
            title: "Package and sell",
            body: "Build releases, include alternate versions, and track direct-to-fan sales across your artists.",
          },
        ]}
      />
      <MarketingSection className="bg-white pt-5 max-[680px]:pt-5">
        <SectionKicker>Built for active catalogs</SectionKicker>
        <SectionTitle>
          See the music and the release work around it.
        </SectionTitle>
        <div className="mt-12 grid grid-cols-[1.1fr_0.9fr] gap-5 max-[980px]:grid-cols-1">
          {SCREENS.map((screen) => (
            <div
              key={screen.src}
              className="overflow-hidden rounded-[30px] border border-marketing-line bg-white p-[14px] shadow-[0_22px_70px_rgba(11,22,72,0.12)]"
            >
              <img
                src={screen.src}
                alt={screen.alt}
                loading="lazy"
                className="max-h-[700px] w-full rounded-[20px] bg-[#F7F8FC] object-contain"
              />
            </div>
          ))}
        </div>
      </MarketingSection>
      <AudiolabSection />
      <SellOverviewSection labels />
      <FaqSection items={FAQ} />
      <RelatedProducts />
      <Footer />
    </main>
  );
}
